import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import api from '../api';
import { foldCallerTurn } from './transcriptFold';
import {
  Phone, PhoneOff, Mic, MicOff, ChevronDown, Loader2, Volume2, MousePointerClick,
} from 'lucide-react';

/*
 * Co-browse widget: the call UI inside the iframe that cobrowse-widget.js
 * mounts on a client's page. During the call the agent can scroll, navigate and
 * fill forms on that page.
 *
 * This iframe never touches the host page itself (a cross-origin iframe
 * cannot). It relays: agent commands arrive on LiveKit data topic TOPIC and go
 * to the loader by postMessage; the loader's results and page snapshots come
 * back the same way. See app/agent/cobrowse.py for the agent side.
 *
 * Page loads on multi-page sites: the loader keeps a first-party record of the
 * live call. On pagehide this iframe leaves the room quietly (no hang-up
 * message), the loader marks the record released, and the widget on the next
 * page rejoins the same room within RESUME_WINDOW_MS. The agent holds the call
 * for that long. A deliberate hang-up is announced first so it ends at once.
 */

const TOPIC = 'braincx.cobrowse';
const HOST_SOURCE = 'bcx-cobrowse-host';
const WIDGET_SOURCE = 'bcx-cobrowse-widget';
// Under the agent's 60s hold (REJOIN_GRACE_SECONDS in app/agent/cobrowse.py)
const RESUME_WINDOW_MS = 55 * 1000;
const HELLO_INTERVAL_MS = 1500;
const HELLO_MAX_TRIES = 20;
const MAX_SAVED_CAPTIONS = 60;

const CLOSED_SIZE = { width: 88, height: 88 };
const CLOSED_LABEL_SIZE = { width: 260, height: 88 };
const OPEN_SIZE = { width: 372, height: 540 };

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : '99, 102, 241';
};

const normalizeColor = (hex, fallback = '#6366f1') => {
  if (!hex || typeof hex !== 'string') return fallback;
  const h = hex.startsWith('#') ? hex : `#${hex}`;
  return /^#[0-9A-Fa-f]{6}$/.test(h) ? h : fallback;
};

const micErrorMessage = (err) => {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access was blocked. Allow it for this site, then try again.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No microphone was found on this device.';
    case 'NotReadableError':
    case 'AbortError':
      return 'Your microphone is in use by another app. Close it and try again.';
    default:
      return err?.message || 'Could not access your microphone.';
  }
};

const preflightMicrophone = async () => {
  if (!navigator.mediaDevices?.getUserMedia) return 'Voice calls need a secure (https) connection.';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return null;
  } catch (err) {
    return micErrorMessage(err);
  }
};

const formatDuration = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const encode = (obj) => new TextEncoder().encode(JSON.stringify(obj));

// How long a rejoined room may look empty before the call counts as over.
// Right after connect the agent can still be on its way in.
const REJOIN_SETTLE_MS = 2500;
const waitForParticipant = (room, ms) => new Promise((resolve) => {
  if (room.remoteParticipants.size > 0) { resolve(true); return; }
  let timer = null;
  const done = (value) => {
    clearTimeout(timer);
    room.off(RoomEvent.ParticipantConnected, onJoin);
    resolve(value);
  };
  const onJoin = () => done(true);
  timer = setTimeout(() => done(room.remoteParticipants.size > 0), ms);
  room.on(RoomEvent.ParticipantConnected, onJoin);
});

// LiveKit caps a reliable data message at about 15 KB. A link-heavy page can
// pass that, so trim the snapshot (least useful parts first) to stay under.
const MAX_PAGE_BYTES = 12000;
const fitPage = (page) => {
  if (!page) return page;
  const p = { ...page };
  const size = () => encode(p).length;
  if (size() <= MAX_PAGE_BYTES) return p;
  p.text = (p.text || '').slice(0, 400);
  p.on_screen = (p.on_screen || '').slice(0, 600);
  while (size() > MAX_PAGE_BYTES && (p.links || []).length > 12) {
    p.links = p.links.slice(0, Math.floor(p.links.length * 0.7));
  }
  if (size() > MAX_PAGE_BYTES) p.buttons = (p.buttons || []).slice(0, 15);
  if (size() > MAX_PAGE_BYTES) p.headings = (p.headings || []).slice(0, 12);
  if (size() > MAX_PAGE_BYTES) p.forms = (p.forms || []).slice(0, 2);
  // Still over: a message past the limit is dropped whole, and the agent loses
  // the page entirely. Trim inside the forms, then whatever is left.
  if (size() > MAX_PAGE_BYTES) {
    p.forms = (p.forms || []).map((f) => ({
      ...f,
      fields: (f.fields || []).slice(0, 15).map((fl) => (fl.options ? { ...fl, options: fl.options.slice(0, 8) } : fl)),
    }));
  }
  if (size() > MAX_PAGE_BYTES) { p.sections = []; p.embeds = []; p.text = ''; }
  if (size() > MAX_PAGE_BYTES) p.forms = [];
  if (size() > MAX_PAGE_BYTES) { p.links = (p.links || []).slice(0, 5); p.on_screen = ''; }
  return p;
};

const CobrowseWidget = ({ agentId }) => {
  const [agent, setAgent] = useState(null);
  const [available, setAvailable] = useState(null); // null = checking
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | connecting | live | resuming
  const [isMuted, setIsMuted] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [callError, setCallError] = useState(null);
  const [captions, setCaptions] = useState([]);
  const [duration, setDuration] = useState(0);
  const [hostReady, setHostReady] = useState(false);
  const [controlStopped, setControlStopped] = useState(false);

  const roomRef = useRef(null);
  const audioRef = useRef(null);
  const captionsEndRef = useRef(null);
  const recordRef = useRef(null);
  const latestPageRef = useRef(null);
  const controlStoppedRef = useRef(false);
  const leavingRef = useRef(false);
  const hangingUpRef = useRef(false);
  const resumingRef = useRef(false);
  const helloTimerRef = useRef(null);
  const hostOriginRef = useRef(null);
  const framed = typeof window !== 'undefined' && window.parent && window.parent !== window;

  const claimedOrigin = (() => {
    try { return new URLSearchParams(window.location.search).get('origin') || null; } catch (_) { return null; }
  })();

  // ── Host page messaging ──────────────────────────────────────────────────
  const postHost = useCallback((msg) => {
    if (!framed) return;
    // The call record (with its resume key) only ever goes to the verified host
    if ((msg.type === 'store' || msg.type === 'command') && !hostOriginRef.current) return;
    try {
      // Before the loader answered we only say "ready", which carries nothing
      window.parent.postMessage({ ...msg, source: WIDGET_SOURCE }, hostOriginRef.current || '*');
    } catch (_) {}
  }, [framed]);

  const publish = useCallback(async (msg) => {
    const room = roomRef.current;
    if (!room || room.state !== 'connected') return;
    try {
      const out = msg.page ? { ...msg, page: fitPage(msg.page) } : msg;
      await room.localParticipant.publishData(encode(out), { reliable: true, topic: TOPIC });
    } catch (_) {}
  }, []);

  const saveRecord = useCallback((patch) => {
    if (!recordRef.current && !patch?.roomName) return;
    recordRef.current = { ...(recordRef.current || {}), ...patch };
    postHost({ type: 'store', record: recordRef.current });
  }, [postHost]);

  const clearRecord = useCallback(() => {
    recordRef.current = null;
    postHost({ type: 'clear' });
    postHost({ type: 'call_state', active: false });
  }, [postHost]);

  // ── Load agent + availability ────────────────────────────────────────────
  useEffect(() => {
    if (!agentId) return;
    api.get(`/agents/${agentId}`).then((r) => setAgent(r.data)).catch(() => setAvailable(false));
    api.get(`/cobrowse/config/${agentId}`, { params: { origin: claimedOrigin || undefined } })
      .then((r) => setAvailable(Boolean(r.data?.enabled && r.data?.origin_allowed)))
      .catch(() => setAvailable(false));
  }, [agentId, claimedOrigin]);

  // Transparent page so only the launcher/card shows over the client's site
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById('root')];
    els.forEach((el) => { if (el) el.style.background = 'transparent'; });
  }, []);

  // ── Size the iframe ──────────────────────────────────────────────────────
  const showLabel = agent?.voice_widget_show_label ?? true;
  useEffect(() => {
    if (available === false) {
      postHost({ type: 'resize', width: 0, height: 0 });
      return;
    }
    const size = isOpen ? OPEN_SIZE : (showLabel && phase === 'idle' ? CLOSED_LABEL_SIZE : CLOSED_SIZE);
    postHost({ type: 'resize', ...size, position: agent?.voice_widget_button_position === 'bottom-left' ? 'bottom-left' : 'bottom-right' });
  }, [isOpen, phase, available, showLabel, agent, postHost, hostReady]);

  // ── Captions ─────────────────────────────────────────────────────────────
  const addCaption = useCallback((speaker, text) => {
    if (!text?.trim()) return;
    setCaptions((prev) => {
      const last = prev[prev.length - 1];
      if (last?.speaker === speaker && last?.text === text) return prev;
      const next = [...prev, { speaker, text }];
      // The caller's whole turn arrives after its parts (transcriptFold.js)
      return (speaker === 'user' ? foldCallerTurn(next, next.length - 1) : next).slice(-MAX_SAVED_CAPTIONS);
    });
  }, []);

  useEffect(() => {
    captionsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (recordRef.current) saveRecord({ captions });
  }, [captions, saveRecord]);

  // ── Duration (from the call's start, so it survives page loads) ──────────
  useEffect(() => {
    if (phase !== 'live') return undefined;
    const tick = () => {
      const startedAt = recordRef.current?.startedAt || Date.now();
      setDuration(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [phase]);

  // ── Hello to the agent (repeated until acknowledged) ─────────────────────
  const stopHello = useCallback(() => {
    clearInterval(helloTimerRef.current);
    helloTimerRef.current = null;
  }, []);

  const startHello = useCallback(() => {
    stopHello();
    let tries = 0;
    const send = () => {
      tries += 1;
      if (tries > HELLO_MAX_TRIES) { stopHello(); return; }
      const room = roomRef.current;
      publish({
        type: 'hello',
        page: latestPageRef.current,
        audio_ok: room ? room.canPlaybackAudio : true,
        control_stopped: controlStoppedRef.current,
      });
    };
    send();
    helloTimerRef.current = setInterval(send, HELLO_INTERVAL_MS);
  }, [publish, stopHello]);

  // ── Join a room (new call or resumed) ────────────────────────────────────
  const joinRoom = useCallback(async ({ url, token }) => {
    const room = new Room({ adaptiveStream: true, dynacast: true, disconnectOnPageLeave: false });

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === 'audio' && audioRef.current) track.attach(audioRef.current);
    });

    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      setAudioBlocked(!room.canPlaybackAudio);
      publish({ type: 'audio', ok: room.canPlaybackAudio });
    });

    room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
      let data;
      try { data = JSON.parse(new TextDecoder().decode(payload)); } catch (_) { return; }
      if (topic === TOPIC) {
        if (data.type === 'command') {
          postHost({ type: 'command', command: { id: data.id, action: data.action, args: data.args || {} } });
        } else if (data.type === 'hello_ack') {
          stopHello();
        }
        return;
      }
      if (data.type === 'transcription' || data.type === 'conversation_item') {
        addCaption(data.speaker || 'agent', data.text);
      }
    });

    room.on(RoomEvent.ParticipantConnected, () => startHello());

    room.on(RoomEvent.Disconnected, () => {
      stopHello();
      setAudioBlocked(false);
      roomRef.current = null;
      if (leavingRef.current) return; // page load: the next page picks the call up
      setPhase('idle');
      setIsMuted(false);
      clearRecord();
    });

    await room.connect(url, token);
    roomRef.current = room;

    try {
      await room.localParticipant.setMicrophoneEnabled(true);
    } catch (micErr) {
      await room.localParticipant.publishData(encode({ type: 'hangup' }), { reliable: true, topic: TOPIC }).catch(() => {});
      await room.disconnect().catch(() => {});
      roomRef.current = null;
      const e = new Error(micErrorMessage(micErr));
      e.isMicError = true;
      throw e;
    }

    // Attach anything already published (the agent may have joined first)
    room.remoteParticipants.forEach((p) => {
      p.audioTrackPublications.forEach((pub) => {
        if (pub.track && audioRef.current) pub.track.attach(audioRef.current);
      });
    });
    setAudioBlocked(!room.canPlaybackAudio);
    if (room.remoteParticipants.size > 0) startHello();
    return room;
  }, [addCaption, clearRecord, postHost, publish, startHello, stopHello]);

  // ── Start a call ─────────────────────────────────────────────────────────
  const startCall = async () => {
    if (!agent || phase !== 'idle') return;
    setCallError(null);
    setCaptions([]);
    setPhase('connecting');
    postHost({ type: 'snapshot_request' });

    const micBlocked = await preflightMicrophone();
    if (micBlocked) {
      setCallError(micBlocked);
      setPhase('idle');
      return;
    }

    let started = null;
    try {
      const res = await api.post('/cobrowse/start', {
        agent_id: agent.id,
        host_origin: hostOriginRef.current || claimedOrigin,
        page_url: latestPageRef.current?.url || null,
      });
      started = res.data;
      recordRef.current = null;
      saveRecord({
        active: true,
        roomName: started.room_name,
        sessionId: started.session_id,
        resumeKey: started.resume_key,
        startedAt: Date.now(),
        captions: [],
        releasedAt: null,
      });
      await joinRoom({ url: started.url, token: started.token });
      setPhase('live');
      postHost({ type: 'call_state', active: true });
    } catch (err) {
      setCallError(err.response?.data?.detail || err.message || 'Could not start the call');
      setPhase('idle');
      if (started?.session_id) api.post(`/sessions/${started.session_id}/end`).catch(() => {});
      clearRecord();
    }
  };

  // ── Resume a call released by the previous page ──────────────────────────
  const resumeCall = useCallback(async (record) => {
    if (resumingRef.current || roomRef.current) return;
    if (!record?.roomName || !record.releasedAt || Date.now() - record.releasedAt > RESUME_WINDOW_MS) return;
    resumingRef.current = true;
    leavingRef.current = false;
    // Claim it, so a second tab or a reload cannot pick the same call up
    recordRef.current = { ...record, releasedAt: null };
    postHost({ type: 'store', record: recordRef.current });
    setCaptions(record.captions || []);
    setIsOpen(true);
    setPhase('resuming');
    setCallError(null);

    try {
      const res = await api.post('/cobrowse/rejoin', {
        session_id: record.sessionId,
        room_name: record.roomName,
        resume_key: record.resumeKey,
        host_origin: hostOriginRef.current || claimedOrigin,
      });
      const room = await joinRoom({ url: res.data.url, token: res.data.token });
      // LiveKit creates a room on join, so one that stays empty means the
      // call ended while the page was loading.
      if (!(await waitForParticipant(room, REJOIN_SETTLE_MS))) {
        leavingRef.current = true; // no "call ended" flash from this disconnect
        await room.disconnect().catch(() => {});
        roomRef.current = null;
        leavingRef.current = false;
        throw new Error('ended');
      }
      setPhase('live');
      postHost({ type: 'call_state', active: true });
    } catch (err) {
      // Why, for whoever is debugging a dropped call (status 410 = the call had
      // already ended on the server, "ended" = nobody in the room)
      console.warn('[BrainCX co-browse] could not continue the call:', err?.response?.status || '', err?.response?.data?.detail || err?.message);
      setPhase('idle');
      setCallError(err?.isMicError ? err.message : 'Your call ended while the page was loading. You can start a new one.');
      clearRecord();
    } finally {
      resumingRef.current = false;
    }
  }, [claimedOrigin, clearRecord, joinRoom, postHost]);

  // ── End the call ─────────────────────────────────────────────────────────
  const endCall = async () => {
    const room = roomRef.current;
    const sessionId = recordRef.current?.sessionId;
    hangingUpRef.current = true;
    stopHello();
    clearRecord();
    try {
      if (room) {
        // A hang-up, not a page load: the agent ends the call now
        await Promise.race([
          room.localParticipant.publishData(encode({ type: 'hangup' }), { reliable: true, topic: TOPIC }),
          new Promise((r) => setTimeout(r, 500)),
        ]).catch(() => {});
        await room.disconnect();
      }
      if (sessionId) await api.post(`/sessions/${sessionId}/end`).catch(() => {});
    } finally {
      roomRef.current = null;
      hangingUpRef.current = false;
      setPhase('idle');
      setIsMuted(false);
      setAudioBlocked(false);
      setDuration(0);
    }
  };

  const toggleMute = async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !isMuted;
    await room.localParticipant.setMicrophoneEnabled(!next);
    setIsMuted(next);
  };

  const resumeAudio = async () => {
    const room = roomRef.current;
    if (!room) return;
    try { await room.startAudio(); } catch (_) {}
    setAudioBlocked(!room.canPlaybackAudio);
    publish({ type: 'audio', ok: room.canPlaybackAudio });
  };

  // ── Messages from the loader ─────────────────────────────────────────────
  useEffect(() => {
    if (!framed) return undefined;
    const onMessage = (event) => {
      if (event.source !== window.parent) return;
      const d = event.data;
      if (!d || d.source !== HOST_SOURCE) return;
      // The first answer fixes the host origin; it must match what the loader
      // put in our URL. Everything after must come from that same origin.
      if (!hostOriginRef.current) {
        if (claimedOrigin && event.origin !== claimedOrigin) return;
        hostOriginRef.current = event.origin;
      } else if (event.origin !== hostOriginRef.current) {
        return;
      }

      switch (d.type) {
        case 'init':
          setHostReady(true);
          if (d.page) latestPageRef.current = d.page;
          controlStoppedRef.current = !!d.control_stopped;
          setControlStopped(!!d.control_stopped);
          if (d.record) resumeCall(d.record);
          break;
        case 'result':
          publish({ type: 'result', id: d.id, ok: d.ok, error: d.error, message: d.message, data: d.data, page: d.page });
          break;
        case 'snapshot':
          if (d.page) latestPageRef.current = d.page;
          publish({ type: 'snapshot', page: d.page, reason: d.reason });
          break;
        case 'control':
          controlStoppedRef.current = !!d.stopped;
          setControlStopped(!!d.stopped);
          publish({ type: 'control', stopped: !!d.stopped });
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    postHost({ type: 'ready' });
    return () => window.removeEventListener('message', onMessage);
  }, [framed, claimedOrigin, postHost, publish, resumeCall]);

  // ── Hand the call on when the page goes away ─────────────────────────────
  useEffect(() => {
    const onPageHide = () => {
      if (!roomRef.current) return;
      // Leave quietly (no hang-up message) so the agent holds the call while
      // the next page loads. The loader marks the call record released.
      leavingRef.current = true;
      stopHello();
      roomRef.current.disconnect();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [stopHello]);

  // An unmount mid-call that is not a page load: end it like a hang-up
  useEffect(() => () => {
    const room = roomRef.current;
    if (room && !leavingRef.current) {
      room.localParticipant.publishData(encode({ type: 'hangup' }), { reliable: true, topic: TOPIC })
        .catch(() => {})
        .finally(() => room.disconnect());
    }
    clearInterval(helloTimerRef.current);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────
  if (available === false || available === null || !agent) {
    return <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />;
  }

  const isDark = agent.voice_widget_theme_mode === 'dark';
  const primary = normalizeColor(agent.voice_widget_primary_color);
  const buttonColor = normalizeColor(agent.voice_widget_button_color);
  const rgb = hexToRgb(primary);
  const title = agent.voice_widget_title || agent.name || 'Voice Assistant';
  const label = agent.voice_widget_button_label || 'Talk to us';
  const welcome = agent.voice_widget_welcome_message ||
    'I can talk you through this page, open the right sections and help fill in forms. Start a call when you are ready.';
  const bg = isDark ? '#1e1e2e' : '#ffffff';
  const bg2 = isDark ? '#2a2a3e' : '#f4f5f9';
  const text = isDark ? '#e2e8f0' : '#1a202c';
  const muted = isDark ? '#94a3b8' : '#64748b';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const live = phase === 'live';
  const busy = phase === 'connecting' || phase === 'resuming';
  const alignRight = agent.voice_widget_button_position !== 'bottom-left';

  const status = live ? `Connected · ${formatDuration(duration)}`
    : phase === 'resuming' ? 'Reconnecting after page load…'
    : phase === 'connecting' ? 'Connecting…'
    : 'Ready when you are';

  return (
    <div style={{ position: 'fixed', inset: 0, fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
      <style>{`
        @keyframes cbSpin { to { transform: rotate(360deg); } }
        @keyframes cbPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(${hexToRgb(buttonColor)},0.45); } 50% { box-shadow: 0 0 0 10px rgba(${hexToRgb(buttonColor)},0); } }
        @keyframes cbBar { 0%,100% { transform: scaleY(.35); } 50% { transform: scaleY(1); } }
      `}</style>
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />

      {isOpen ? (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          background: bg, color: text, borderRadius: 18, overflow: 'hidden',
          border: `1px solid ${border}`, boxShadow: '0 18px 60px rgba(0,0,0,0.22)',
        }}>
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${primary}, ${buttonColor})`, color: '#fff',
            padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{status}</div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Minimize"
              style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8, padding: 6, color: '#fff', cursor: 'pointer', display: 'flex' }}
            >
              <ChevronDown size={16} />
            </button>
          </div>

          {/* Tap to hear (browser blocked autoplay after a page load) */}
          {live && audioBlocked && (
            <button
              type="button"
              onClick={resumeAudio}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '9px 12px',
                border: 'none', borderBottom: `1px solid ${border}`, background: `rgba(${rgb},0.14)`,
                color: text, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              <Volume2 size={16} /> Tap to hear the assistant
            </button>
          )}

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, background: bg }}>
            {!live && !busy && captions.length === 0 && (
              <div style={{ background: `rgba(${rgb},0.08)`, border: `1px solid rgba(${rgb},0.2)`, borderRadius: 12, padding: '10px 12px', fontSize: 13, lineHeight: 1.5 }}>
                {welcome}
              </div>
            )}
            {(live || busy) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: muted }}>
                <MousePointerClick size={14} />
                {controlStopped ? 'Page control is off. The assistant will describe where to click.' : 'The assistant can scroll and guide this page for you.'}
              </div>
            )}
            {callError && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '8px 12px', color: '#ef4444', fontSize: 12 }}>
                {callError}
              </div>
            )}
            {!framed && (
              <div style={{ fontSize: 12, color: muted }}>
                Page guidance only works when this widget is installed with its script tag.
              </div>
            )}
            {captions.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: c.speaker === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '85%', padding: '8px 12px', fontSize: 13, lineHeight: 1.5,
                  borderRadius: c.speaker === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: c.speaker === 'user' ? `rgba(${rgb},0.15)` : bg2,
                  border: `1px solid ${c.speaker === 'user' ? `rgba(${rgb},0.25)` : border}`,
                }}>
                  {c.text}
                </div>
              </div>
            ))}
            <div ref={captionsEndRef} />
          </div>

          {/* Controls */}
          <div style={{ borderTop: `1px solid ${border}`, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, background: bg }}>
            {live ? (
              <>
                <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 22 }} aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} style={{
                      width: 4, height: 18, borderRadius: 4, background: primary, opacity: isMuted ? 0.3 : 1,
                      animation: isMuted ? 'none' : `cbBar ${0.9 + i * 0.15}s ease-in-out infinite`,
                    }} />
                  ))}
                </div>
                <button type="button" onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'} style={{
                  width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: isMuted ? 'rgba(239,68,68,0.15)' : `rgba(${rgb},0.12)`, color: isMuted ? '#ef4444' : primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                </button>
                <button type="button" onClick={endCall} title="End call" style={{
                  width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: '#ef4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(239,68,68,0.4)',
                }}>
                  <PhoneOff size={20} />
                </button>
              </>
            ) : (
              <button type="button" onClick={startCall} disabled={busy || (framed && !hostReady)} title="Start call" style={{
                height: 48, padding: '0 22px', borderRadius: 999, border: 'none',
                cursor: busy ? 'default' : 'pointer', background: busy ? '#94a3b8' : buttonColor, color: '#fff',
                display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14,
                boxShadow: busy ? 'none' : `0 4px 18px rgba(${hexToRgb(buttonColor)},0.4)`,
              }}>
                {busy ? <Loader2 size={18} style={{ animation: 'cbSpin 1s linear infinite' }} /> : <Phone size={18} />}
                {busy ? 'Connecting' : 'Start call'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          position: 'absolute', bottom: 12, [alignRight ? 'right' : 'left']: 12,
          display: 'flex', alignItems: 'center', gap: 10, flexDirection: alignRight ? 'row' : 'row-reverse',
        }}>
          {showLabel && phase === 'idle' && (
            <div style={{
              background: isDark ? '#2a2a3e' : '#fff', color: text, border: `1px solid ${border}`, borderRadius: 20,
              padding: '6px 14px', fontSize: 13, fontWeight: 600, boxShadow: '0 2px 12px rgba(0,0,0,0.12)', whiteSpace: 'nowrap',
            }}>
              {label}
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label={live ? 'Open call' : 'Open voice assistant'}
            style={{
              width: 60, height: 60, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: live ? '#16a34a' : buttonColor, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 20px rgba(${hexToRgb(buttonColor)},0.45)`,
              animation: phase === 'idle' && (agent.voice_widget_pulse_animation ?? true) ? 'cbPulse 2s ease-in-out infinite' : 'none',
            }}
          >
            {live && audioBlocked ? <Volume2 size={24} /> : <Phone size={24} />}
          </button>
        </div>
      )}
    </div>
  );
};

export const CobrowseWidgetPage = ({ agentId }) => <CobrowseWidget agentId={agentId} />;

export default CobrowseWidget;
